/**
 * Official colour themes and device-level layout preferences.
 *
 * Palettes are pure token sets (index.css keys them off html[data-palette]),
 * so every screen shares one layout system and one component library — a
 * palette never forks UI code. Navigation layout is a separate, device-level
 * choice. Both live in Settings → Appearance.
 */

export type PaletteId = "folio" | "folio-sky" | "folio-focus" | "paper" | "sky" | "focus";
const PALETTE_IDS: PaletteId[] = ["folio", "folio-sky", "folio-focus", "paper", "sky", "focus"];

const SIGNED_OUT_APPEARANCE_KEY = "novari:device-appearance";
export function rememberSignedOutAppearance() {
  const root = document.documentElement;
  try {
    localStorage.setItem(SIGNED_OUT_APPEARANCE_KEY, JSON.stringify({
      palette: resolvePalette(root.getAttribute("data-palette")),
      mode: root.classList.contains("dark") ? "dark" : "light",
      kids: root.getAttribute("data-kids-mode") === "true",
    }));
  } catch { /* Appearance can remain session-only when storage is unavailable. */ }
}

export function loadSignedOutAppearance(): { palette: PaletteId; mode: "light" | "dark"; kids: boolean } | null {
  try {
    const value = JSON.parse(localStorage.getItem(SIGNED_OUT_APPEARANCE_KEY) || "null");
    if (!value || !PALETTE_IDS.includes(value.palette) || !["light", "dark"].includes(value.mode)) return null;
    return { palette: value.palette, mode: value.mode, kids: value.kids === true };
  } catch { return null; }
}

export type ThemeId = "studio" | "paper";
export type ColourId = "ember" | "sky" | "green";

export interface PaletteOption {
  id: PaletteId;
  theme: ThemeId;
  colour: ColourId;
  name: string;
  /** Swatches for the picker: [ground, surface, accent, highlight] per mode. */
  swatches: { light: [string, string, string, string]; dark: [string, string, string, string] };
}

/** New accounts persist this key in users.preferences; legacy fallback stays Paper. */
export const DEFAULT_SIGNUP_PALETTE: PaletteId = "folio";

const EMBER_STUDIO = { light: ["#F4F1EA", "#FCFAF5", "#B84F30", "#3F7D57"], dark: ["#171510", "#211E18", "#ED9976", "#8DB99A"] } as const;
const EMBER_PAPER = { light: ["#F4F1EA", "#FFFFFF", "#C9573A", "#3F7D57"], dark: ["#13110E", "#1C1915", "#EC8A62", "#7FB58F"] } as const;
const SKY = { light: ["#EEF1F8", "#FFFFFF", "#4B5BD7", "#F2B51D"], dark: ["#0F121C", "#171B28", "#8E9BFF", "#F5C54A"] } as const;
const GREEN = { light: ["#F6F6F4", "#FFFFFF", "#1E7A58", "#2B2B28"], dark: ["#111211", "#191A19", "#5CC296", "#E6E6E1"] } as const;

/** Two looks (themes), each in three colours. The saved value is the palette id. */
export const PALETTES: PaletteOption[] = [
  { id: "folio", theme: "studio", colour: "ember", name: "Studio · Ember", swatches: { light: [...EMBER_STUDIO.light], dark: [...EMBER_STUDIO.dark] } },
  { id: "folio-sky", theme: "studio", colour: "sky", name: "Studio · Sky", swatches: { light: [EMBER_STUDIO.light[0], EMBER_STUDIO.light[1], SKY.light[2], SKY.light[3]], dark: [EMBER_STUDIO.dark[0], EMBER_STUDIO.dark[1], SKY.dark[2], SKY.dark[3]] } },
  { id: "folio-focus", theme: "studio", colour: "green", name: "Studio · Green", swatches: { light: [EMBER_STUDIO.light[0], EMBER_STUDIO.light[1], GREEN.light[2], GREEN.light[3]], dark: [EMBER_STUDIO.dark[0], EMBER_STUDIO.dark[1], GREEN.dark[2], GREEN.dark[3]] } },
  { id: "paper", theme: "paper", colour: "ember", name: "Paper · Ember", swatches: { light: [...EMBER_PAPER.light], dark: [...EMBER_PAPER.dark] } },
  { id: "sky", theme: "paper", colour: "sky", name: "Paper · Sky", swatches: { light: [...SKY.light], dark: [...SKY.dark] } },
  { id: "focus", theme: "paper", colour: "green", name: "Paper · Green", swatches: { light: [...GREEN.light], dark: [...GREEN.dark] } },
];

export const THEMES: { id: ThemeId; name: string; description: string }[] = [
  { id: "studio", name: "Studio", description: "Soft, rounded and friendly. The same look as novaristudy.site." },
  { id: "paper", name: "Paper", description: "Warm and editorial. Low glare for long reading sessions." },
];

export const COLOUR_NAMES: Record<ColourId, string> = { ember: "Ember", sky: "Sky", green: "Green" };

export function paletteFor(theme: ThemeId, colour: ColourId): PaletteOption {
  return PALETTES.find((p) => p.theme === theme && p.colour === colour) ?? PALETTES[0];
}

const LEGACY_TO_PALETTE: Record<string, PaletteId> = {
  INK_EMBER: "paper",
  MINIMALIST: "paper",
  MONOCHROME: "paper",
  HIGH_CONTRAST: "paper",
  MIDNIGHT_TEAL: "paper",
};

/** Accounts saved on retired theme keys all resolve to Paper. */
export function resolvePalette(uiTheme?: string | null): PaletteId {
  if (!uiTheme) return "paper";
  if (PALETTE_IDS.includes(uiTheme as PaletteId)) return uiTheme as PaletteId;
  return LEGACY_TO_PALETTE[uiTheme] ?? "paper";
}

export type NavLayout = "sidebar" | "topbar";

export const NAV_LAYOUTS: { id: NavLayout; name: string; description: string }[] = [
  { id: "sidebar", name: "Sidebar", description: "Navigation in a panel on the left." },
  { id: "topbar", name: "Top bar", description: "Navigation across the top. More room for content." },
];

const NAV_LAYOUT_KEY = "novari:nav-layout";

export function loadNavLayout(): NavLayout {
  try {
    const v = localStorage.getItem(NAV_LAYOUT_KEY);
    return v === "topbar" ? "topbar" : "sidebar";
  } catch {
    return "sidebar";
  }
}

export function saveNavLayout(layout: NavLayout) {
  try {
    localStorage.setItem(NAV_LAYOUT_KEY, layout);
  } catch {
    /* storage unavailable — choice lasts for this session only */
  }
  window.dispatchEvent(new CustomEvent("novari:nav-layout", { detail: layout }));
}

export function applyPalette(palette: PaletteId) {
  document.documentElement.setAttribute("data-palette", palette);
}

export type AnswerStyle = "cards" | "compact" | "grid";

export const ANSWER_STYLES: { id: AnswerStyle; name: string; description: string }[] = [
  { id: "cards", name: "Cards", description: "Large, easy-to-tap answer cards." },
  { id: "compact", name: "List", description: "Tight lettered rows. Faster to scan." },
  { id: "grid", name: "Grid", description: "Two columns on wider screens." },
];

const ANSWER_STYLE_KEY = "novari:answer-style";

export function loadAnswerStyle(): AnswerStyle {
  try {
    const v = localStorage.getItem(ANSWER_STYLE_KEY);
    return v === "compact" || v === "grid" ? v : "cards";
  } catch {
    return "cards";
  }
}

export function saveAnswerStyle(style: AnswerStyle) {
  try {
    localStorage.setItem(ANSWER_STYLE_KEY, style);
  } catch {
    /* session only */
  }
  window.dispatchEvent(new CustomEvent("novari:answer-style", { detail: style }));
}
